/**
 * What a run comes to, figure by figure, at the foot of «Correr planilla» (the owner, 2026-10-31:
 * «when we hit run payroll can we see a calculation summary/breakdown of the payroll somewhere in that
 * dialog?»): each person's pay down to what is handed over (`pasosDeLaLinea`), then what the run costs
 * the business (`pasosDelCosto`) and what of that nobody is handed today. Read off the books' own
 * lines, so it says what the run will book, and it follows what is typed on the lines above it.
 */
import { Fragment } from 'react';
import { formatMoney, subMoney, type Centavos, type FrecuenciaPago, type Rango, type Settings } from '@abastece/shared';
import type { Empleado, PlanillaLinea } from '@abastece/core';
import { pasosDeLaLinea, pasosDelCosto, totalesDeCorrida, type PasoDeLaPlanilla } from '../lib/planilla.js';
import { useT } from '../i18n/index.js';

/** A line each, as on a receipt: which way it goes, what it is, how much; a total in bold. */
function Recibo({ pasos }: { pasos: readonly PasoDeLaPlanilla[] }) {
  const t = useT();
  return (
    <div className="grid grid-cols-[0.75rem_minmax(0,1fr)_auto] gap-x-1.5 gap-y-0.5" data-desglose-pasos>
      {pasos.map((p, i) => (
        <Fragment key={i}>
          <span className="text-slate-500">{p.signo}</span>
          <span className={p.signo === '=' ? 'font-semibold text-slate-100' : 'text-slate-300'}>{t(p.clave, p.valores)}</span>
          <span className={`tabular text-right whitespace-nowrap ${p.signo === '=' ? 'font-semibold text-slate-100' : ''}`}>
            {formatMoney(p.monto)}
          </span>
        </Fragment>
      ))}
    </div>
  );
}

export function DesglosePlanilla({
  personas,
  frecuencia,
  rango,
  config,
  pagada,
}: {
  /** The lines ready to run; `escrito`: the pay typed on the line. */
  personas: readonly { empleado: Empleado; libros: PlanillaLinea; escrito: boolean }[];
  frecuencia: FrecuenciaPago;
  rango: Rango;
  config: Settings;
  /** «Ya se pagó»: handed over already, not to be. */
  pagada: boolean;
}) {
  const t = useT();
  const tot = totalesDeCorrida(personas.map((p) => p.libros));
  // Not handed to anybody with the pay: what goes to INSS, INATEC and the DGI, and what is set aside.
  const cotizaciones = tot.inssLaboral + tot.inssPatronal + tot.inatec + tot.irRetenido;
  const apartado = tot.provisionAguinaldo + tot.provisionVacaciones;
  return (
    <section className="rounded-xl bg-slate-950/40 p-3 text-sm ring-1 ring-white/5 ring-inset" data-desglose>
      <h3 className="font-semibold text-slate-100">{t('planilla.desglose.titulo')}</h3>
      <div className="mt-3 flex flex-col gap-3">
        {personas.map(({ empleado, libros, escrito }) => (
          <div key={empleado.id} data-desglose-de={empleado.nombre}>
            <p className="mb-1 truncate text-xs font-medium text-slate-400">{empleado.nombre}</p>
            <Recibo pasos={pasosDeLaLinea(empleado, libros, frecuencia, rango, config, { escrito, pagada })} />
          </div>
        ))}
        {personas.length > 1 && (
          <div className="border-t border-white/10 pt-2" data-desglose-entregar>
            <Recibo
              pasos={[
                {
                  signo: '=',
                  clave: pagada ? 'planilla.desglose.entregadoTodos' : 'planilla.desglose.aEntregarTodos',
                  monto: subMoney(tot.neto, tot.adelantos),
                },
              ]}
            />
          </div>
        )}
        <div className="border-t border-white/10 pt-3" data-desglose-costo>
          <p className="mb-1 text-xs font-medium text-slate-400">{t('planilla.desglose.costo')}</p>
          <Recibo pasos={pasosDelCosto(tot, config)} />
        </div>
        {(cotizaciones > 0 || apartado > 0) && (
          <div className="flex flex-col gap-1 text-xs text-slate-500" data-desglose-despues>
            {cotizaciones > 0 && <p>{t('planilla.desglose.cotizacionesDespues', { monto: formatMoney(cotizaciones as Centavos) })}</p>}
            {/* Vacations off for the shop (2026-10-31): only the aguinaldo is set aside. */}
            {apartado > 0 && (
              <p>
                {tot.provisionVacaciones > 0
                  ? t('planilla.desglose.apartadoDespues', { monto: formatMoney(apartado as Centavos) })
                  : t('planilla.desglose.aguinaldoDespues', { monto: formatMoney(apartado as Centavos) })}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
